<template>
  <div class="min-h-screen bg-gray-50">
 
    
    <!-- Admin Layout -->
    <AppSidebar 
      v-if="isAdminView" 
      :title="'Order Details'" 
      :description="order ? `Order #${order.order_number}` : 'Loading...'"
    >
      <template #header-actions>
        <div class="flex space-x-3" v-if="order">
          <router-link :to="authStore.user?.role === 'admin' ? '/dashboard/admin/orders' : '/dashboard/developer/orders'" class="btn btn-outline">
             Back to Orders
          </router-link>
          <button 
            v-if="['completed', 'processing'].includes(order.status)"
            @click="viewInvoice"
            class="btn btn-outline"
          >
            View Invoice
          </button>
          
          <div class="relative inline-block text-left">
             <button @click="openStatusModal" class="btn btn-primary">
                Update Status
             </button>
          </div>
        </div>
      </template>

      <div class="max-w-7xl mx-auto">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-16">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p class="text-gray-600">Loading order details...</p>
        </div>

        <!-- Order Details Content (Shared) -->
        <OrderContent 
          v-else-if="order"
          :order="order" 
          :orderTimeline="orderTimeline" 
          :isAdminView="isAdminView"
          :canManageTeam="authStore.user?.role === 'admin'"
          :userStore="userStore"
          :selectedDeveloperId="selectedDeveloperId"
          :formatDate="formatDate"
          :getEstimatedDelivery="getEstimatedDelivery"
          :getStatusClass="getStatusClass"
          :formatStatus="formatStatus"
          :getProgressPercentage="getProgressPercentage"
          :getProgressDescription="getProgressDescription"
          :calculateTotal="calculateTotal"
          :calculateTax="calculateTax"
          @viewInvoice="viewInvoice"
          @downloadInvoice="downloadInvoice"
          @proceedToPayment="proceedToPayment"
          @downloadOrderSummary="downloadOrderSummary"
          @contactSupport="contactSupport"
          @assignDeveloper="assignDeveloper"
          @removeDeveloper="removeDeveloper"
          @updateStatus="openStatusModal"
          @update:selectedDeveloperId="selectedDeveloperId = $event"
          :newMessage="newMessage"
          @update:newMessage="newMessage = $event"
          @sendMessage="sendMessage"
        />

        <!-- Error State -->
        <div v-else class="text-center py-16">
           <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">Order Not Found</h3>
          <p class="text-gray-600 mb-4">The requested order could not be found.</p>
          <router-link :to="authStore.user?.role === 'admin' ? '/dashboard/admin/orders' : '/dashboard/developer/orders'" class="btn btn-primary">
            Back to Orders
          </router-link>
        </div>
      </div>
    </AppSidebar>
    
    <!-- Client Layout -->
    <AppSidebar 
      v-else 
      :title="'Order Details'" 
      :description="order ? `Order #${order.order_number}` : 'Loading...'"
    >
      <template #header-actions>
        <div class="flex space-x-3" v-if="order">
          <router-link to="/dashboard/client/orders" class="btn btn-outline">
            ← Back to Orders
          </router-link>
          <button 
            v-if="['completed', 'processing'].includes(order.status)"
            @click="downloadInvoice"
            class="btn btn-outline flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            Download Invoice
          </button>
          <button 
            v-if="['approved', 'quote_sent'].includes(order.status)"
            @click="proceedToPayment"
            class="btn btn-primary"
          >
            Pay Now
          </button>
          <button 
            @click="contactSupport"
            class="btn bg-green-500 hover:bg-green-600 text-white flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            Support
          </button>
        </div>
      </template>

      <div class="max-w-7xl mx-auto">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-16">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p class="text-gray-600">Loading order details...</p>
        </div>

        <!-- Order Details Content (Shared) -->
        <OrderContent 
          v-else-if="order"
          :order="order" 
          :orderTimeline="orderTimeline" 
          :isAdminView="isAdminView"
          :userStore="userStore"
          :selectedDeveloperId="selectedDeveloperId"
          :formatDate="formatDate"
          :getEstimatedDelivery="getEstimatedDelivery"
          :getStatusClass="getStatusClass"
          :formatStatus="formatStatus"
          :getProgressPercentage="getProgressPercentage"
          :getProgressDescription="getProgressDescription"
          :calculateTotal="calculateTotal"
          :calculateTax="calculateTax"
          @viewInvoice="viewInvoice"
          @downloadInvoice="downloadInvoice"
          @proceedToPayment="proceedToPayment"
          @downloadOrderSummary="downloadOrderSummary"
          @contactSupport="contactSupport"
          @assignDeveloper="assignDeveloper"
          @removeDeveloper="removeDeveloper"
          @updateStatus="openStatusModal"
          @update:selectedDeveloperId="selectedDeveloperId = $event"
          :newMessage="newMessage"
          @update:newMessage="newMessage = $event"
          @sendMessage="sendMessage"
        />

        <!-- Error State -->
        <div v-else class="text-center py-16">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">Order Not Found</h3>
          <p class="text-gray-600 mb-4">The requested order could not be found.</p>
          <router-link to="/dashboard/client/orders" class="btn btn-primary">
            Back to Orders
          </router-link>
        </div>
      </div>
    </AppSidebar>

    <AppFooter v-if="!isAdminView" />

    <!-- Status Update Modal -->
    <div v-if="showStatusModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" @click="showStatusModal = false"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">Update Order Status</h3>
                <div class="mt-4">
                  <label for="status" class="block text-sm font-medium text-gray-700">New Status</label>
                  <select v-model="newStatus" id="status" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md">
                    <option v-for="status in statusOptions" :key="status.value" :value="status.value">
                      {{ status.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button @click="confirmStatusUpdate" type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm">
              Update
            </button>
            <button @click="showStatusModal = false" type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrderStore } from '@/stores/orders'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/users'
import { useUIStore } from '@/stores/ui'
import axios from '@/utils/axios'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import OrderContent from '@/components/shared/OrderContent.vue'

const route = useRoute()
const router = useRouter()
const orderStore = useOrderStore()
const authStore = useAuthStore()
const userStore = useUserStore()
const uiStore = useUIStore()

// State
const order = ref(null)
const loading = ref(true)
const newMessage = ref('')
const taxRate = ref(8.25)
const selectedDeveloperId = ref('')
const showStatusModal = ref(false)
const newStatus = ref('')

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'quote_sent', label: 'Quote Sent' },
  { value: 'approved', label: 'Approved' },
  { value: 'processing', label: 'Processing' },
  { value: 'review', label: 'In Review' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
]

// Computed
const isAdminView = computed(() => {
  return ['admin', 'developer'].includes(authStore.user?.role)
})

const orderTimeline = computed(() => {
  if (!order.value) return []
  
  const timeline = [
    {
      title: 'Order Placed',
      description: 'Order has been submitted and is awaiting review',
      date: order.value.created_at,
      completed: true,
      current: false
    },
    {
      title: 'Under Review',
      description: 'Our team is reviewing your requirements',
      date: order.value.reviewed_at,
      completed: ['quote_sent', 'approved', 'processing', 'completed'].includes(order.value.status),
      current: order.value.status === 'pending'
    },
    {
      title: 'Quote Sent',
      description: 'Project quote has been prepared and sent',
      date: order.value.quote_sent_at,
      completed: ['approved', 'processing', 'completed'].includes(order.value.status),
      current: order.value.status === 'quote_sent'
    },
    {
      title: 'Payment Received',
      description: 'Payment has been processed successfully',
      date: order.value.payment_date,
      completed: ['processing', 'completed'].includes(order.value.status),
      current: order.value.status === 'approved'
    },
    {
      title: 'In Progress',
      description: 'Development work has started',
      date: order.value.started_at,
      completed: order.value.status === 'completed',
      current: order.value.status === 'processing'
    },
    {
      title: 'Completed',
      description: 'Project has been delivered successfully',
      date: order.value.completed_at,
      completed: order.value.status === 'completed',
      current: false
    }
  ]
  
  return timeline
})

// Methods
const calculateTax = () => {
  if (!order.value) return 0
  const subtotal = order.value.subtotal || order.value.total_amount || 0
  return Math.round(subtotal * (taxRate.value / 100) * 100) / 100
}

const calculateTotal = () => {
  if (!order.value) return 0
  const subtotal = order.value.subtotal || order.value.total_amount || 0
  return subtotal + calculateTax()
}

const getStatusClass = (status) => {
  const classes = {
    draft: 'bg-gray-100 text-gray-800',
    pending: 'bg-yellow-100 text-yellow-800',
    quote_sent: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    processing: 'bg-purple-100 text-purple-800',
    review: 'bg-orange-100 text-orange-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

const formatStatus = (status) => {
  if (!status) return 'N/A'
  return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getEstimatedDelivery = () => {
  if (!order.value) return 'N/A'
  
  const timeline = order.value.requirements?.timeline_preference
  if (!timeline) return 'To be determined'
  
  const createdDate = new Date(order.value.created_at)
  let deliveryDate = new Date(createdDate)
  
  switch (timeline) {
    case 'rush':
      deliveryDate.setDate(createdDate.getDate() + 7)
      break
    case 'standard':
      deliveryDate.setDate(createdDate.getDate() + 21)
      break
    case 'extended':
      deliveryDate.setDate(createdDate.getDate() + 45)
      break
    default:
      return 'To be determined'
  }
  
  return formatDate(deliveryDate)
}

const getProgressPercentage = () => {
  if (!order.value) return 0
  
  const statusProgress = {
    draft: 10,
    pending: 20,
    quote_sent: 40,
    approved: 50,
    processing: 80,
    review: 90,
    completed: 100,
    cancelled: 0
  }
  
  return statusProgress[order.value.status] || 0
}

const getProgressDescription = () => {
  const progress = getProgressPercentage()
  
  if (progress === 100) return 'Project completed successfully!'
  if (progress >= 80) return 'Final review and testing in progress'
  if (progress >= 50) return 'Development work in progress'
  if (progress >= 40) return 'Waiting for payment confirmation'
  if (progress >= 20) return 'Quote preparation in progress'
  return 'Order submitted and under review'
}

const viewInvoice = () => {
  router.push(`/orders/${order.value.id}/invoice`)
}

const downloadInvoice = async () => {
  if (!order.value) return
  
  try {
    await orderStore.downloadInvoice(order.value.id)
  } catch (error) {
    console.error('Failed to download invoice:', error)
  }
}

const proceedToPayment = () => {
  router.push(`/orders/${order.value.id}/pay`)
}

// Quick Actions Implementation
const openStatusModal = () => {
  newStatus.value = order.value.status
  showStatusModal.value = true
}

const confirmStatusUpdate = async () => {
  try {
    const updatedOrder = await orderStore.updateOrderStatus(order.value.id, newStatus.value)
    order.value = updatedOrder
    showStatusModal.value = false
    uiStore.showNotification({
      type: 'success',
      title: 'Status Updated',
      message: `Order status has been updated to ${formatStatus(newStatus.value)}`
    })
  } catch (error) {
    uiStore.showNotification({
      type: 'error',
      title: 'Update Failed',
      message: error.message || 'Failed to update order status'
    })
  }
}

const downloadOrderSummary = () => {
  window.print()
}

const contactSupport = () => {
  const message = `Hi! I need support for my Order #${order.value.order_number}.\n\n` +
    `📋 *Service:* ${order.value.service_name || 'N/A'}\n` +
    `🔢 *Order ID:* ${order.value.id}\n` +
    `📍 *Status:* ${formatStatus(order.value.status)}\n\n` +
    `Please help me with...`
  orderStore.openWhatsApp(message)
}

const assignDeveloper = async () => {
  if (!selectedDeveloperId.value) return
  
  try {
    await orderStore.assignDeveloper(order.value.id, selectedDeveloperId.value)
    
    // Refresh order details to show new assignment
    const { data } = await axios.get(`/orders/${order.value.id}`)
    order.value = data
    
    selectedDeveloperId.value = ''
    uiStore.showNotification({
      type: 'success',
      title: 'Developer Assigned',
      message: 'Developer successfully assigned to project'
    })
  } catch (error) {
    uiStore.showNotification({
      type: 'error',
      title: 'Assignment Failed',
      message: error.response?.data?.message || 'Failed to assign developer'
    })
  }
}

const removeDeveloper = async (developerId) => {
  if (!confirm('Are you sure you want to remove this developer from the project?')) return
  
  try {
    await orderStore.removeDeveloper(order.value.id, developerId)
    
    // Refresh order details
    const { data } = await axios.get(`/orders/${order.value.id}`)
    order.value = data
    
    uiStore.showNotification({
      type: 'success',
      title: 'Developer Removed',
      message: 'Developer successfully removed from project'
    })
  } catch (error) {
    uiStore.showNotification({
      type: 'error',
      title: 'Removal Failed',
      message: error.response?.data?.message || 'Failed to remove developer'
    })
  }
}

const sendMessage = async () => {
  if (!newMessage.value.trim()) return
  
  try {
    // Add message logic here
    uiStore.showNotification({
      type: 'success',
      title: 'Message Sent',
      message: 'Your message has been sent to the client.'
    })
    newMessage.value = ''
  } catch (error) {
    console.error('Failed to send message:', error)
  }
}

const fetchOrder = async () => {
  loading.value = true
  try {
    const { data } = await axios.get(`/orders/${route.params.id}`)
    order.value = data
    
    // If admin view, fetch developers list for assignment
    if (isAdminView.value) {
      await userStore.fetchDevelopers()
    }
  } catch (error) {
    console.error('Failed to fetch order:', error)
    if (error.response?.status === 404) {
      order.value = null
    } else {
        uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load order details'
        })
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchOrder()
})
</script>
